import { HStack, Stack } from "@chakra-ui/react";
import { intervalToDuration } from "date-fns/intervalToDuration";
import { useTranslation } from "next-i18next";
import { Address } from "viem";
import { Button, LowEthIcon, NextLink, Text } from "@looksrare/uikit";
import { formatDuration } from "@looksrare/utils";
import { addSeconds } from "date-fns/addSeconds";
import { getIsRakebackFullyPaused, usePausedContractCurrencySymbols, useUserRakebackData } from "@/hooks/rakeback";

interface ProfileRakebackRowProps {
  address?: Address;
}

export const ProfileRakebackRow = ({ address }: ProfileRakebackRowProps) => {
  const { t } = useTranslation();
  const now = new Date();

  const { data: userRakebackData } = useUserRakebackData(address!, { enabled: !!address });
  const { data: pausedContractCurrencySymbols } = usePausedContractCurrencySymbols();

  const nextRakeback = userRakebackData?.nextRakeback;

  const isRakebackPaused = getIsRakebackFullyPaused(pausedContractCurrencySymbols) || !nextRakeback;
  const isNextRakebackUnlocked = nextRakeback?.status === "UNLOCKED";

  const rakebackClosesIn = intervalToDuration({
    start: now,
    end: new Date(nextRakeback?.expiresAt ?? now),
  });

  return (
    <HStack spacing={4} p={2} width="100%">
      <LowEthIcon boxSize={5} />
      <Stack spacing={0} overflow="hidden" flex={1}>
        <Text textStyle="detail" flexGrow={1}>
          {t("Rakeback")}
        </Text>
        {isNextRakebackUnlocked && (
          <Text
            width="100%"
            overflow="hidden"
            textStyle="caption"
            textOverflow="ellipsis"
            color="text-warning"
            whiteSpace="nowrap"
            bold
          >
            {t("{{time}} Remaining", {
              time: formatDuration(rakebackClosesIn, {
                format: ["days", "hours", "minutes"],
                shortLabels: true,
              }),
            })}
          </Text>
        )}
      </Stack>
      <Button
        as={NextLink}
        href="/rakeback"
        size="xs"
        colorScheme={isRakebackPaused || !isNextRakebackUnlocked ? "secondary" : "acid"}
      >
        {(() => {
          if (isRakebackPaused) {
            return t("Calculating...");
          }

          if (isNextRakebackUnlocked) {
            return t("Claim Now");
          }

          const nextRakebackUnlocksAt = new Date(nextRakeback.unlocksAt);

          const nextRakebackUnlocksIn = intervalToDuration({
            start: now,
            end: nextRakebackUnlocksAt,
          });

          const isUnlockingSoon = nextRakebackUnlocksAt < addSeconds(now, 60);

          return t("Next: {{time}}", {
            time: isUnlockingSoon
              ? t("Soon")
              : formatDuration(nextRakebackUnlocksIn, {
                  format: ["days", "hours", "minutes"],
                  shortLabels: true,
                }),
          });
        })()}
      </Button>
    </HStack>
  );
};
